import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

import { fetchCostCenters } from '../forms/forms.apis';
import {
  createActivity,
  fetchActivityDetail,
  fetchActivityParents,
  fetchActivityTypes,
  updateActivity,
} from './activity.apis';
import { ILookupItem } from '../forms/forms.models';
import { IActivityForm } from './activity.models';

type Props = {
  activityix?: string;
  navigate?: (path: string) => void;
};

type State = {
  loading: boolean;
  saving: boolean;
  error: string;
  success: string;

  form: IActivityForm;
  activityTypes: ILookupItem[];
  parentActivities: ILookupItem[];
  costcenters: ILookupItem[];
};

export default class ActivityEditPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      saving: false,
      error: '',
      success: '',

      form: this.getEmptyForm(),
      activityTypes: [],
      parentActivities: [],
      costcenters: [],
    };
  }

  componentDidMount(): void {
    this.loadInitialData();
  }

  private getEmptyForm = (): IActivityForm => {
    return {
      recname: '',
      shortcode: '',
      description: '',
      typeix: '',
      parentix: '',
      costcenterix: '',
      status: 1,
    };
  };

  private loadInitialData = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [activityTypes, parentActivities, costcenters] = await Promise.all([
        fetchActivityTypes(),
        fetchActivityParents(),
        fetchCostCenters(),
      ]);

      this.setState(
        {
          activityTypes,
          parentActivities,
          costcenters,
        },
        async () => {
          if (this.props.activityix) {
            await this.loadDetail(this.props.activityix);
          } else {
            this.setState({ loading: false });
          }
        }
      );
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load activity editor',
      });
    }
  };

  private loadDetail = async (activityix: string): Promise<void> => {
    try {
      const data = await fetchActivityDetail(activityix);

      const form: IActivityForm = {
        recix: data.activity.recix,
        recname: data.activity.recname || '',
        shortcode: data.activity.shortcode || '',
        description: data.activity.description || '',
        typeix: data.activity.type?.recix || '',
        parentix: data.activity.parent?.recix || '',
        costcenterix: data.activity.costcenter?.recix || '',
        status: data.activity.status ?? 1,
      };

      this.setState({
        form,
        loading: false,
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load activity detail',
      });
    }
  };

  private handleTextChange =
    (field: keyof IActivityForm) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;
      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          [field]: value,
        },
      }));
    };

  private handleSelectChange =
    (field: keyof IActivityForm) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;
      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          [field]: field === 'status' ? Number(value) : value,
        },
      }));
    };

  private validate = (): string[] => {
    const { form } = this.state;
    const errors: string[] = [];

    if (!form.recname.trim()) errors.push('Activity name is required.');
    if (!form.shortcode.trim()) errors.push('Activity code is required.');
    if (!form.description.trim()) errors.push('Description is required.');
    if (!form.typeix) errors.push('Activity type is required.');
    if (!form.costcenterix) errors.push('Cost center is required.');

    return errors;
  };

  private handleSave = async (): Promise<void> => {
    const errors = this.validate();

    if (errors.length > 0) {
      this.setState({
        error: errors.join(' '),
        success: '',
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    try {
      const result = this.props.activityix
        ? await updateActivity(this.props.activityix, this.state.form)
        : await createActivity(this.state.form);

      this.setState({
        saving: false,
        success: 'Activity saved successfully.',
      });

      const activityix = result?.activityix || this.props.activityix;
      if (!this.props.activityix && activityix) {
        const path = `/forms/activities/${activityix}/edit`;

        if (this.props.navigate) {
          this.props.navigate(path);
          return;
        }

        window.location.href = path;
      }
    } catch (error: any) {
      this.setState({
        saving: false,
        error: error?.message || 'Failed to save activity',
      });
    }
  };

  private handleBack = (): void => {
    const path = '/forms/activities';

    if (this.props.navigate) {
      this.props.navigate(path);
      return;
    }

    window.location.href = path;
  };

  render(): React.ReactNode {
    const {
      loading,
      saving,
      error,
      success,
      form,
      activityTypes,
      parentActivities,
      costcenters,
    } = this.state;

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {this.props.activityix ? 'Edit Activity' : 'New Activity'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create or update ABB activity masters.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={this.handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={this.handleSave}
              disabled={saving || loading}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        ) : null}

        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Activity Name"
                  value={form.recname}
                  onChange={this.handleTextChange('recname')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Code"
                  value={form.shortcode}
                  onChange={this.handleTextChange('shortcode')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-status-label">Status</InputLabel>
                  <Select
                    labelId="activity-status-label"
                    value={`${form.status}`}
                    label="Status"
                    onChange={this.handleSelectChange('status')}
                  >
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  minRows={3}
                  value={form.description}
                  onChange={this.handleTextChange('description')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-type-label">Activity Type</InputLabel>
                  <Select
                    labelId="activity-type-label"
                    value={form.typeix}
                    label="Activity Type"
                    onChange={this.handleSelectChange('typeix')}
                  >
                    {activityTypes.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-parent-label">Parent Activity</InputLabel>
                  <Select
                    labelId="activity-parent-label"
                    value={form.parentix}
                    label="Parent Activity"
                    onChange={this.handleSelectChange('parentix')}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {parentActivities
                      .filter((item) => item.recix !== form.recix)
                      .map((item) => (
                        <MenuItem key={item.recix} value={item.recix}>
                          {item.recname}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-costcenter-label">Cost Center</InputLabel>
                  <Select
                    labelId="activity-costcenter-label"
                    value={form.costcenterix}
                    label="Cost Center"
                    onChange={this.handleSelectChange('costcenterix')}
                  >
                    {costcenters.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }
}
